import { createContext, useContext, useEffect, useState, useRef } from "react";
import type { ReactNode } from "react";
import { useLogout, useCurrentUser, useSendOtp, useVerifyOtp, useSetUniversity, useSetName } from '@src/presentation/hooks/users';
import type {
    UserResponseDTO,
    ProfileStateType,
    SendOtpResponseDTO,
    VerifyOtpResponseDTO,
} from '@src/application/users/dto/auth-response.dto';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
    isAuthenticated: boolean;
    user: UserResponseDTO | null;
    profileState: ProfileStateType | null;
    isProfileComplete: boolean;
    // OTP methods (primary authentication)
    sendOtp: (email: string) => Promise<SendOtpResponseDTO>;
    verifyOtp: (email: string, code: string) => Promise<VerifyOtpResponseDTO>;
    setUniversity: (universityId: string) => Promise<ProfileStateType>;
    setName: (firstName: string, lastName: string, middleName?: string) => Promise<ProfileStateType>;
    logout: () => void;
    loading: boolean;
    initializing: boolean; // True during initial user fetch
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function AuthProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();

    // Auth state managed via useState
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<UserResponseDTO | null>(null);
    const [profileState, setProfileState] = useState<ProfileStateType | null>(null);

    // Track if we're in the middle of an auth operation (prevents sync effect from resetting state)
    const isAuthOperationInProgress = useRef(false);

    const logoutMutation = useLogout();
    const sendOtpMutation = useSendOtp();
    const verifyOtpMutation = useVerifyOtp();
    const setUniversityMutation = useSetUniversity();
    const setNameMutation = useSetName();

    // Fetch current user - syncs with local state
    const {
        data: currentUser,
        isLoading: isLoadingUser,
        error: currentUserError,
        refetch: refetchUser,
        isFetched: hasAttemptedFetch,
        isSuccess: querySuccess,
    } = useCurrentUser();

    // Sync local state with query result
    // Only update state when we have definitive results and no auth operation is in progress
    useEffect(() => {
        // Skip sync if we're in the middle of an auth operation
        if (isAuthOperationInProgress.current) {
            return;
        }

        if (currentUser) {
            // Query succeeded with user data
            setIsAuthenticated(true);
            setUser(currentUser);
            setProfileState(currentUser.profileState);
        } else if (hasAttemptedFetch && !isLoadingUser && currentUserError) {
            // Query failed, we've attempted fetch, and not loading
            setIsAuthenticated(false);
            setUser(null);
            setProfileState(null);
        }
    }, [currentUser, isLoadingUser, currentUserError, hasAttemptedFetch, querySuccess]);

    // Derived state
    const isProfileComplete = profileState === 'ACTIVE';

    // Log auth state changes in dev mode
    useEffect(() => {
        if (import.meta.env.DEV) {
            console.log('🔐 Auth State:', {
                isAuthenticated,
                hasUser: !!user,
                profileState,
                isLoading: isLoadingUser,
                hasError: !!currentUserError,
                hasAttemptedFetch,
            });
        }
    }, [user, isLoadingUser, currentUserError, isAuthenticated, profileState, hasAttemptedFetch]);

    const sendOtp = async (email: string): Promise<SendOtpResponseDTO> => {
        if (import.meta.env.DEV) {
            console.log('📧 Sending OTP to:', email);
        }

        const result = await sendOtpMutation.mutateAsync({ email });

        if (import.meta.env.DEV) {
            console.log('✅ OTP sent successfully');
        }

        return result;
    };

    const verifyOtp = async (email: string, code: string): Promise<VerifyOtpResponseDTO> => {
        if (import.meta.env.DEV) {
            console.log('🔐 Verifying OTP...');
        }

        // Mark that we're in an auth operation to prevent sync effect from resetting state
        isAuthOperationInProgress.current = true;

        try {
            const result = await verifyOtpMutation.mutateAsync({ email, code });

            if (import.meta.env.DEV) {
                console.log('✅ OTP verified successfully:', {
                    isNewUser: result.isNewUser,
                    profileState: result.profileState,
                });
            }

            // Immediately update auth state after successful OTP verification
            setIsAuthenticated(true);
            setProfileState(result.profileState);

            // Remove old query data and refetch to get full user data
            await queryClient.removeQueries({ queryKey: ['auth', 'currentUser'] });
            const { data } = await refetchUser();

            if (data) {
                setUser(data);
                setProfileState(data.profileState);
            }

            if (import.meta.env.DEV) {
                console.log('🔄 User refetched after OTP verification:', data);
            }

            return result;
        } finally {
            // Always reset the flag when operation completes
            isAuthOperationInProgress.current = false;
        }
    };

    const setUniversity = async (universityId: string): Promise<ProfileStateType> => {
        if (import.meta.env.DEV) {
            console.log('🏫 Setting university...');
        }

        const result = await setUniversityMutation.mutateAsync({ universityId });

        if (import.meta.env.DEV) {
            console.log('✅ University set successfully, new profileState:', result.user.profileState);
        }

        // Update local state immediately
        setUser(result.user);
        setProfileState(result.user.profileState);

        // Sync query cache
        queryClient.setQueryData(['auth', 'currentUser'], result.user);

        return result.user.profileState;
    };

    const setName = async (firstName: string, lastName: string, middleName?: string): Promise<ProfileStateType> => {
        if (import.meta.env.DEV) {
            console.log('👤 Setting name...');
        }

        const result = await setNameMutation.mutateAsync({ firstName, lastName, middleName });

        if (import.meta.env.DEV) {
            console.log('✅ Name set successfully, new profileState:', result.user.profileState);
        }

        // Update local state immediately
        setUser(result.user);
        setProfileState(result.user.profileState);

        // Sync query cache
        queryClient.setQueryData(['auth', 'currentUser'], result.user);

        return result.user.profileState;
    };

    const refreshUser = async (): Promise<void> => {
        if (import.meta.env.DEV) {
            console.log('🔄 Refreshing user data...');
        }
        const { data } = await refetchUser();
        if (data) {
            setUser(data);
            setProfileState(data.profileState);
            setIsAuthenticated(true);
        }
    };

    const logout = (): void => {
        logoutMutation.mutate();
        // Clear local state immediately
        setIsAuthenticated(false);
        setUser(null);
        setProfileState(null);
        // Clear query cache
        queryClient.removeQueries({ queryKey: ['auth'] });
        queryClient.removeQueries({ queryKey: ['credits'] });
    };

    // Loading states (exclude onboarding mutations - they handle their own loading)
    const loading = logoutMutation.isPending || sendOtpMutation.isPending ||
        verifyOtpMutation.isPending;

    // Initial auth check is in progress
    const initializing = isLoadingUser && !hasAttemptedFetch;

    const value = {
        isAuthenticated,
        user,
        profileState,
        isProfileComplete,
        sendOtp,
        verifyOtp,
        setUniversity,
        setName,
        logout,
        loading,
        initializing,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use the Auth context
function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export { AuthProvider, useAuth };
export type { AuthContextType };
